/**
 * PSTUDIO-4240: what an ENTRANCE into the Workflows page is allowed to conclude.
 *
 * PSTUDIO-4215 closed the entrance that goes through an explicit solution pick:
 * that pick re-applies `initialBotRelatedContext` (`draftVersion: null`), which
 * makes the route → store rules release the identity, so the next entry assigns
 * a CHANGED id and the reaction fetches. The two gestures this file exists for
 * dispatch no `selectBot` at all — browser Back onto `/:projectId/:botId/workflows`,
 * and Workflows clicked from a page inside the same solution — so `draftVersion`
 * survives, `buildSolutionVersionId` recomposes the same string, and assigning an
 * unchanged value is a no-op for a mobx reaction. No fetch is issued.
 *
 * Builder's mount effect cannot fix that by itself: it can only ASSIGN
 * (`pages/builder.tsx`), and its unmount must not be given the job either —
 * forcing the subscription cleanup on the way out drops the two waiting flags
 * that gate Publish, which would let a user publish a version the backend has
 * not finished setting up. So the responsibility belongs to the way IN, and this
 * is the rule that decides it.
 *
 * It lives beside `table-store-route-sync.ts` for the same reason that file
 * exists: a decision made inline in a React effect is harder to reach from a
 * test than the same decision in a pure function. "Harder", not impossible —
 * the package renders React in jsdom in 16 `*.test.tsx` files; what blocks a
 * render of `pages/builder.tsx` specifically is its `@pypestream/design-system`
 * import, whose Lit custom elements do not survive that environment. The
 * wiring is tested one layer up, in `hooks/use-builder-entry`, which takes the
 * store as an argument and so imports none of that.
 */

/** The part of `tableStore` this rule reads. */
export interface BuilderEntryTarget {
  solutionVersionId: string;
  isWaitingForBackendData: boolean;
  /** Data arrived, the live corpus id has not. Gates Publish just as the flag above does. */
  isWaitingForLiveCorpusIdOnly: boolean;
  /**
   * The last visit ended without ever applying data — the fetch or the
   * subscription failed. The waiting flags are DOWN in that state, so without
   * this the rule would read a failed visit as a healthy one.
   */
  versionVisitFailed: boolean;
  /** Starts a new visit of the id the store already holds (`stores/table-store.ts`). */
  restartVersionVisit(): void;
}

export interface BuilderEntryInput {
  /** `buildSolutionVersionId(...)` for the entrance — `''` while the context is unresolved. */
  solutionVersionId: string;
}

/**
 * Restart the version's visit when — and only when — this entrance walks into a
 * store that is still parked waiting on the very id being entered.
 *
 * The three refusals are as load-bearing as the one action:
 *
 * - **Neither waiting NOR failed** → the rows are in memory and the table the
 *   user returns to is populated, so the missing fetch is invisible (stale at
 *   worst). Re-fetching anyway would put a `GetProStudioVersionData` on every
 *   single navigation into Workflows, which is a real cost for a rare
 *   entrance.
 *
 *   The second half of that condition is the one review argued for, and it is
 *   load-bearing: "not waiting" is NOT the same as "healthy". Five arms in
 *   `stores/table-store.ts` lower `isWaitingForBackendData` over an EMPTY
 *   table — the live-corpus subscription failing to initialise, the
 *   no-initial-data subscription reporting an error, a throw while handling one
 *   of its pushes, that subscription failing to initialise, and the initial
 *   fetch's `.catch`. Each leaves the same screen the ticket describes (0
 *   nodes, no spinner, no error) with the flags DOWN. `versionVisitFailed`
 *   (raised by those five, reset at the start of every visit) is what lets this
 *   rule serve them too, so the retry is the user walking back in rather than a
 *   hard reload.
 * - **A different version** → on the wiring in `hooks/use-builder-entry` this
 *   arm is not the one that fires: the assignment effect runs first, so the
 *   two ids already MATCH by the time the rule reads the store, and what
 *   refuses is the waiting-flag check below (the reaction's synchronous forced
 *   cleanup has just lowered both). The id comparison is the belt to that pair
 *   of braces — it covers a frame where the assignment's own guard did not
 *   pass (an absent `projectId`, say) while an id is already in hand. Restart
 *   there and the fetch would go out for the id the STORE holds, not the one
 *   being entered.
 * - **No id yet** → the first frames after a POP navigation arrive before the
 *   XState context resolves, so both sides are `''` and an equality test alone
 *   would read that as "the store is already on it".
 *
 * @returns whether a new visit was requested (the tests' observable outcome).
 */
export function applyBuilderEntry(
  store: BuilderEntryTarget,
  { solutionVersionId }: BuilderEntryInput
): boolean {
  if (!solutionVersionId || store.solutionVersionId !== solutionVersionId) {
    return false;
  }

  if (
    !store.isWaitingForBackendData &&
    !store.isWaitingForLiveCorpusIdOnly &&
    !store.versionVisitFailed
  ) {
    return false;
  }

  store.restartVersionVisit();
  return true;
}
